// src/components/admin/AdminDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CheckSquare, Users, MessageSquare, CreditCard, DollarSign, Clock,
    Calendar, FileText, UserCheck, Calendar as CalendarIcon, TrendingUp, Award,
    HeadphonesIcon, Plane, BarChart3, Shield, Store, Database,
    Settings, UserPlus, Briefcase, ClipboardList, Upload, Building, Search, GripVertical,
    Megaphone, Home, AlertCircle, ListTodo
} from 'lucide-react';
import attendanceService from '../../services/attendance.service';
import vibeService from '../../services/vibe.service';
import employeeService from '../../services/employee.service';
import { useAuth } from '../../hooks/useAuth';
import CelebrationHub from '../common/vibe/CelebrationHub';
import PersonalTasksWidget from '../common/tasks/PersonalTasksWidget';
import TeamTasksWidget from '../common/tasks/TeamTasksWidget';
import { toISTDateString, getCurrentISTDate, formatISTDate, getISTComponents } from '../../utils/dateHelpers';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isDragging, setIsDragging] = useState(false);
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedOver, setDraggedOver] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const longPressTimer = useRef(null);
    const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });

    // State for Today's Attendance
    const [todayAttendance, setTodayAttendance] = useState([]);
    const [attendanceLoading, setAttendanceLoading] = useState(true);
    const [totalEmployees, setTotalEmployees] = useState(0);
    const [employees, setEmployees] = useState([]);

    // State for Announcements
    const [announcements, setAnnouncements] = useState([]);
    const [announcementsLoading, setAnnouncementsLoading] = useState(true);

    // State for Badge Counts
    const [badgeCounts, setBadgeCounts] = useState({
        taskbox: 0,
        vibe: 0,
        teamTaskbox: 0
    });

    // State for Vibe Visit Tracking
    const [lastVibeVisit, setLastVibeVisit] = useState(() => {
        const saved = localStorage.getItem('adminLastVibeVisit');
        return saved ? new Date(saved) : null;
    });

    // State for current date and time
    const [currentDate, setCurrentDate] = useState(new Date());

    // State for clock format (true = 24-hour, false = 12-hour)
    const [is24HourFormat, setIs24HourFormat] = useState(true);

    // Icon mapping object - ensure all icons are properly mapped
    const iconMap = {
        'CheckSquare': CheckSquare,
        'Users': Users,
        'MessageSquare': MessageSquare,
        'CreditCard': CreditCard,
        'DollarSign': DollarSign,
        'Clock': Clock,
        'Calendar': Calendar,
        'FileText': FileText,
        'UserCheck': UserCheck,
        'CalendarIcon': CalendarIcon,  // This is the aliased Calendar import
        'TrendingUp': TrendingUp,
        'Award': Award,
        'HeadphonesIcon': HeadphonesIcon,
        'Plane': Plane,
        'BarChart3': BarChart3,
        'Shield': Shield,
        'Store': Store,
        'Database': Database,
        'Settings': Settings,
        'UserPlus': UserPlus,
        'Briefcase': Briefcase,
        'ClipboardList': ClipboardList,
        'Upload': Upload,
        'Building': Building,
        'ListTodo': ListTodo,
        'Search': Search,
        'GripVertical': GripVertical
    };

    // Initial modules data with icon names as strings (matching original code exactly)
    const initialMyAccessModules = [
        {
            id: 'taskbox',
            title: 'Task Box',
            icon: 'CheckSquare',
            bgColor: 'bg-gray-100',
            iconColor: 'text-gray-700',
            route: '/admin/taskbox',
            badge: badgeCounts.taskbox
        },
        {
            id: 'team-taskbox',
            title: 'Team Taskbox',
            icon: 'ListTodo',
            bgColor: 'bg-gray-100',
            iconColor: 'text-gray-700',
            route: '/admin/team-taskbox',
            badge: badgeCounts.teamTaskbox
        },
        {
            id: 'employees',
            title: 'Employees',
            icon: 'Users',
            bgColor: 'bg-blue-100',
            iconColor: 'text-blue-700',
            route: '/admin/employees'
        },
        {
            id: 'vibe',
            title: 'Vibe',
            icon: 'MessageSquare',
            bgColor: 'bg-purple-100',
            iconColor: 'text-purple-700',
            route: '/admin/vibe',
            badge: badgeCounts.vibe
        },
        // {
        //     id: 'reimbursement',
        //     title: 'Reimbursement',
        //     icon: 'CreditCard',
        //     bgColor: 'bg-green-100',
        //     iconColor: 'text-green-700',
        //     route: '/admin/reimbursement'
        // },
        {
            id: 'compensation',
            title: 'Compensation',
            icon: 'DollarSign',
            bgColor: 'bg-yellow-100',
            iconColor: 'text-yellow-700',
            route: '/admin/payroll'
        },
        {
            id: 'attendance',
            title: 'Attendance',
            icon: 'Clock',
            bgColor: 'bg-red-100',
            iconColor: 'text-red-700',
            route: '/admin/attendance'
        },
        {
            id: 'payrollrun',
            title: 'Payroll',
            icon: 'DollarSign',
            bgColor: 'bg-green-100',
            iconColor: 'text-green-700',
            route: '/admin/payrollrun'
        },
        // {
        //     id: 'leave',
        //     title: 'Leave',
        //     icon: 'Calendar',
        //     bgColor: 'bg-orange-100',
        //     iconColor: 'text-orange-700',
        //     route: '/admin/leaves'
        // },
        {
            id: 'hr-documents',
            title: 'HR Documents',
            icon: 'FileText',
            bgColor: 'bg-teal-100',
            iconColor: 'text-teal-700',
            route: '/admin/documents'
        },
        {
            id: 'onboarding',
            title: 'Onboarding',
            icon: 'UserCheck',
            bgColor: 'bg-pink-100',
            iconColor: 'text-pink-700',
            route: '/admin/onboarding'
        },
        // {
        //     id: 'calendar',
        //     title: 'Calendar',
        //     icon: 'CalendarIcon',  // This is the aliased Calendar icon from your import
        //     bgColor: 'bg-indigo-100',
        //     iconColor: 'text-indigo-700',
        //     route: '/admin/calendar'
        // },
        // {
        //     id: 'performance',
        //     title: 'Performance',
        //     icon: 'TrendingUp',
        //     bgColor: 'bg-purple-100',
        //     iconColor: 'text-purple-700',
        //     route: '/admin/performance'
        // },
        // {
        //     id: 'talent',
        //     title: 'Talent Management',
        //     icon: 'Award',
        //     bgColor: 'bg-blue-100',
        //     iconColor: 'text-blue-700',
        //     route: '/admin/talent'
        // }
    ];

    const initialAdditionalModules = [
        // {
        //     id: 'reports',
        //     title: 'Reports',
        //     icon: 'BarChart3',
        //     bgColor: 'bg-gray-100',
        //     iconColor: 'text-gray-700',
        //     route: '/admin/reports'
        // },
        {
            id: 'reports-builder',
            title: 'Document Builder',
            icon: 'ClipboardList',
            bgColor: 'bg-blue-100',
            iconColor: 'text-blue-700',
            route: '/admin/reports-builder'
        },
        {
            id: 'analytics',
            title: 'Analytics',
            icon: 'BarChart3',
            bgColor: 'bg-purple-100',
            iconColor: 'text-purple-700',
            route: '/admin/analytics-dashboard'
        },
        {
            id: 'org-view',
            title: 'Org View',
            icon: 'Building',
            bgColor: 'bg-green-100',
            iconColor: 'text-green-700',
            route: '/admin/org-view'
        },
        // {
        //     id: 'tax-filing',
        //     title: 'Tax Filing',
        //     icon: 'FileText',
        //     bgColor: 'bg-yellow-100',
        //     iconColor: 'text-yellow-700',
        //     route: '/admin/tax'
        // },
        // {
        //     id: 'visitor',
        //     title: 'Visitor Management',
        //     icon: 'UserCheck',
        //     bgColor: 'bg-blue-100',
        //     iconColor: 'text-blue-700',
        //     route: '/admin/visitors'
        // },
        // {
        //     id: 'analytics-dashboard',
        //     title: 'Analytics Dashboard',
        //     icon: 'BarChart3',
        //     bgColor: 'bg-indigo-100',
        //     iconColor: 'text-indigo-700',
        //     route: '/admin/analytics-dashboard'
        // }
    ];

    const initialAdminAccessModules = [
        // {
        //     id: 'onboarding',
        //     title: 'Onboarding',
        //     icon: 'UserPlus',
        //     bgColor: 'bg-yellow-100',
        //     iconColor: 'text-yellow-700',
        //     route: '/admin/onboarding'
        // },
        // {
        //     id: 'recognition',
        //     title: 'Recognition',
        //     icon: 'Award',
        //     bgColor: 'bg-purple-100',
        //     iconColor: 'text-purple-700',
        //     route: '/admin/recognition'
        // },
        // {
        //     id: 'performance-admin',
        //     title: 'Performance',
        //     icon: 'TrendingUp',
        //     bgColor: 'bg-blue-100',
        //     iconColor: 'text-blue-700',
        //     route: '/admin/performance'
        // },
        // {
        //     id: 'talent-admin',
        //     title: 'Talent Management',
        //     icon: 'Users',
        //     bgColor: 'bg-indigo-100',
        //     iconColor: 'text-indigo-700',
        //     route: '/admin/talent'
        // },
        // {
        //     id: 'position',
        //     title: 'Position Management',
        //     icon: 'Briefcase',
        //     bgColor: 'bg-purple-100',
        //     iconColor: 'text-purple-700',
        //     route: '/admin/positions'
        // },
        // {
        //     id: 'travel',
        //     title: 'Travel & Expense',
        //     icon: 'Plane',
        //     bgColor: 'bg-yellow-100',
        //     iconColor: 'text-yellow-700',
        //     route: '/admin/travel'
        // }
    ];

    // Combine all modules into one array for easier management
    const [allModules, setAllModules] = useState(() => {
        // Try to load saved order from localStorage
        const saved = localStorage.getItem('adminDashboardOrder');
        if (saved) {
            try {
                const savedModules = JSON.parse(saved);
                // Ensure each saved module has all required properties
                // Map saved modules to include icon names if missing
                const defaultModules = [
                    ...initialMyAccessModules,
                    ...initialAdditionalModules,
                    ...initialAdminAccessModules
                ];

                // Create a map of default modules for reference
                const defaultModuleMap = {};
                defaultModules.forEach(module => {
                    defaultModuleMap[module.id] = module;
                });

                // Restore saved order but ensure all properties are present
                return savedModules.map(savedModule => {
                    const defaultModule = defaultModuleMap[savedModule.id];
                    if (defaultModule) {
                        // Merge saved module with default to ensure all properties exist
                        return {
                            ...defaultModule,
                            ...savedModule,
                            icon: defaultModule.icon // Ensure icon is from default
                        };
                    }
                    return savedModule;
                });
            } catch (e) {
                console.error('Failed to parse saved order:', e);
                localStorage.removeItem('adminDashboardOrder'); // Clear corrupted data
            }
        }

        // Otherwise use initial order
        return [
            ...initialMyAccessModules,
            ...initialAdditionalModules,
            ...initialAdminAccessModules
        ];
    });

    // Save order to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('adminDashboardOrder', JSON.stringify(allModules));
    }, [allModules]);

    // Fetch total employee count and employees data
    useEffect(() => {
        const fetchTotalEmployees = async () => {
            try {
                const employeesData = await employeeService.getAllEmployees();
                setTotalEmployees(employeesData.length);
                setEmployees(employeesData);
            } catch (error) {
                console.error('Error fetching total employees:', error);
                setTotalEmployees(0);
                setEmployees([]);
            }
        };

        fetchTotalEmployees();
    }, []);

    // Fetch today's attendance for all employees - using same comprehensive approach as ComprehensiveAttendanceManagement
    useEffect(() => {
        const fetchTodayAttendance = async () => {
            try {
                setAttendanceLoading(true);
                const localDate = getCurrentISTDate();

                console.log('🔍 Admin Dashboard - Fetching comprehensive attendance data for:', localDate);

                // Import required services
                const { api } = await import('../../services/api');
                const holidayService = await import('../../services/holiday.service');

                // Fetch all required data in parallel: attendance, leaves, and holiday status (same as ComprehensiveAttendanceManagement)
                const [attendanceResponse, leaveResponse, holidayResponse] = await Promise.all([
                    api.get(`/admin/attendance?date=${localDate}`),
                    api.get(`/api/leaves/all-requests`), // Get all leave requests
                    holidayService.default.isHoliday(localDate) // Check if selected date is a holiday
                ]);

                const attendanceData = attendanceResponse.data.attendance || [];
                const allLeaveData = leaveResponse.data.leaves || leaveResponse.data.requests || leaveResponse.data || [];
                const isHoliday = holidayResponse.isHoliday || false;
                const holidayDetails = holidayResponse.holiday || null;

                console.log('🔍 Admin Dashboard - Fetched attendance data:', attendanceData.length, 'records');
                console.log('🔍 Admin Dashboard - Fetched all leave requests:', allLeaveData.length, 'records');
                console.log('🎉 Admin Dashboard - Is Holiday:', isHoliday, holidayDetails ? `(${holidayDetails.name})` : '');

                // Process attendance data with leaves and holidays (same logic as ComprehensiveAttendanceManagement)
                const processedAttendance = processAttendanceData(
                    attendanceData,
                    allLeaveData,
                    employees,
                    localDate,
                    isHoliday,
                    holidayDetails
                );

                setTodayAttendance(processedAttendance);
                setAttendanceLoading(false);
            } catch (error) {
                console.error('Error fetching today\'s attendance:', error);
                setTodayAttendance([]);
                setAttendanceLoading(false);
            }
        };

        // Only fetch if we have employees data
        if (employees.length > 0) {
            fetchTodayAttendance();
        }

        // Also refetch when component becomes visible (user navigates back)
        const handleVisibilityChange = () => {
            if (!document.hidden && employees.length > 0) {
                fetchTodayAttendance();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [employees.length]); // Depend on employees.length to ensure employees are loaded

    const getEmployeeId = (record) => {
        if (!record) return null;
        return record.employee_id || record.id || record.emp_id || null;
    };

    // Process attendance data using same logic as ComprehensiveAttendanceManagement
    const processAttendanceData = (attendanceData, allLeaveData, employeesList, selectedDate, isHoliday, holidayDetails) => {
        console.log('📦 Admin Dashboard - Processing attendance data with comprehensive logic');

        // Create a map to store employee status deduplication
        const employeeStatusMap = new Map();
        const selectedDay = new Date(selectedDate);
        if (!Number.isNaN(selectedDay?.getTime?.())) {
            selectedDay.setHours(0, 0, 0, 0);
        } else {
            console.warn('Admin Dashboard - Invalid selected date supplied to processAttendanceData:', selectedDate);
        }

        // Initialize map with all employees to ensure every employee is represented once
        employeesList.forEach(employee => {
            const employeeId = getEmployeeId(employee);
            if (!employeeId) return;

            const joiningDate = employee.joining_date ? new Date(employee.joining_date) : null;
            if (joiningDate) {
                joiningDate.setHours(0, 0, 0, 0);
            }

            const hasValidSelectedDay = !Number.isNaN(selectedDay?.getTime?.());

            let initialStatus = 'absent';
            if (hasValidSelectedDay && joiningDate && selectedDay < joiningDate) {
                initialStatus = 'inactive';
            } else if (isHoliday) {
                initialStatus = 'holiday';
            } else if (hasValidSelectedDay && isWeekOff(selectedDay, employee)) {
                initialStatus = 'week_off';
            }

            employeeStatusMap.set(employeeId, {
                employee_id: employeeId,
                id: employeeId,
                employee_code: employee.employee_code,
                first_name: employee.first_name,
                last_name: employee.last_name,
                employee_name: employee.employee_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim(),
                department_name: employee.department_name,
                status: initialStatus,
                joining_date: employee.joining_date,
                weekly_off: employee.weekly_off,
                check_in_time: null,
                check_out_time: null,
                work_hours: 0,
                work_location: null,
                holidayDetails: initialStatus === 'holiday' ? holidayDetails : null,
                originalSource: initialStatus === 'holiday'
                    ? 'holiday'
                    : initialStatus === 'week_off'
                        ? 'week_off'
                        : initialStatus === 'inactive'
                            ? 'employee'
                            : 'employee'
            });
        });

        // Process attendance records
        attendanceData.forEach(record => {
            const employeeId = getEmployeeId(record);
            if (!employeeId) return;

            const existingRecord = employeeStatusMap.get(employeeId);
            if (existingRecord) {
                if (existingRecord.status !== 'inactive' && existingRecord.status !== 'leave') {
                    employeeStatusMap.set(employeeId, {
                        ...existingRecord,
                        ...record,
                        employee_id: employeeId,
                        id: employeeId,
                        status: 'present',
                        employee_code: record.employee_code || existingRecord.employee_code,
                        first_name: record.first_name || existingRecord.first_name,
                        last_name: record.last_name || existingRecord.last_name,
                        employee_name: record.employee_name || existingRecord.employee_name,
                        department_name: record.department_name || existingRecord.department_name,
                        originalSource: 'attendance',
                        holidayDetails: null
                    });
                }
            } else {
                console.warn('Admin Dashboard - Attendance record for unknown employee ID:', employeeId);
            }
        });

        // Process leave records
        allLeaveData.forEach(leave => {
            const employeeId = getEmployeeId(leave);
            if (!employeeId) return;

            // Check if leave is approved and covers the selected date
            const leaveStatus = leave.status?.toLowerCase();
            if (leaveStatus !== 'approved') return;

            const fromDate = leave.from_date || leave.start_date;
            const toDate = leave.to_date || leave.end_date;
            const leaveFromDate = fromDate ? new Date(fromDate) : null;
            const leaveToDate = toDate ? new Date(toDate) : null;

            if (leaveFromDate) {
                leaveFromDate.setHours(0, 0, 0, 0);
            }
            if (leaveToDate) {
                leaveToDate.setHours(23, 59, 59, 999);
            }

            if (Number.isNaN(leaveFromDate?.getTime?.()) || Number.isNaN(leaveToDate?.getTime?.()) || Number.isNaN(selectedDay?.getTime?.())) {
                return;
            }

            const isLeaveDateCovered = leaveFromDate && leaveToDate &&
                selectedDay >= leaveFromDate && selectedDay <= leaveToDate;

            if (!isLeaveDateCovered) return;

            const existingRecord = employeeStatusMap.get(employeeId);
            if (existingRecord) {
                if (existingRecord.status === 'inactive') {
                    return;
                }

                const nameParts = (leave.employee_name || '').split(' ');
                const firstName = leave.first_name || nameParts[0] || existingRecord.first_name;
                const lastName = leave.last_name || nameParts.slice(1).join(' ') || existingRecord.last_name;

                employeeStatusMap.set(employeeId, {
                    ...existingRecord,
                    status: 'leave',
                    leaveDetails: leave,
                    leave_type: leave.leave_type_name || leave.leave_type,
                    leave_reason: leave.reason,
                    first_name: firstName,
                    last_name: lastName,
                    employee_code: leave.employee_code || existingRecord.employee_code,
                    department_name: leave.department_name || existingRecord.department_name,
                    check_in_time: null,
                    check_out_time: null,
                    work_hours: 0,
                    originalSource: 'leave',
                    holidayDetails: null
                });
            } else {
                console.warn('Admin Dashboard - Leave record for unknown employee ID:', employeeId);
            }
        });

        // Convert map to array - each employee appears exactly once
        const completeAttendanceData = Array.from(employeeStatusMap.values()).map(record => {
            if (record.status === 'holiday') {
                return {
                    ...record,
                    holidayDetails
                };
            }
            const { holidayDetails: _unused, ...rest } = record;
            return rest;
        });

        console.log('✅ Admin Dashboard - Processed comprehensive attendance data:', completeAttendanceData.length, 'unique employees');
        console.log('🔍 Admin Dashboard - Final processed data:', completeAttendanceData);

        return completeAttendanceData;
    };

    // Fetch announcements
    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                setAnnouncementsLoading(true);
                // Fetch announcements from dedicated announcements API
                const response = await vibeService.getAnnouncements(3);

                // API returns {success, announcements}
                if (response && response.success && response.announcements) {
                    setAnnouncements(response.announcements);
                } else {
                    setAnnouncements([]);
                }
                setAnnouncementsLoading(false);
            } catch (error) {
                console.error('Error fetching announcements:', error);
                setAnnouncements([]);
                setAnnouncementsLoading(false);
            }
        };

        fetchAnnouncements();
    }, []);

    // Fetch badge counts
    useEffect(() => {
        const fetchBadgeCounts = async () => {
            try {
                console.log('🔍 Admin Dashboard - Fetching dynamic badge counts...');

                // Fetch personal tasks count
                let personalTaskCount = 0;
                try {
                    const taskService = (await import('../../services/task.service')).default;
                    const personalTaskResponse = await taskService.getTasks({
                        category: 'personal',
                        status: 'pending'
                    });
                    personalTaskCount = personalTaskResponse.count || personalTaskResponse.tasks?.length || 0;
                    console.log('📊 Admin Personal Task count for badge:', personalTaskCount);
                } catch (taskError) {
                    console.warn('Admin Dashboard: Could not fetch personal task count:', taskError);
                }

                // Fetch team tasks count
                let teamTaskCount = 0;
                try {
                    const taskService = (await import('../../services/task.service')).default;
                    const teamTaskResponse = await taskService.getTasks({
                        category: 'team_task',
                        status: 'pending'
                    });
                    teamTaskCount = teamTaskResponse.count || teamTaskResponse.tasks?.length || 0;
                    console.log('👥 Admin Team Task count for badge:', teamTaskCount);
                } catch (teamTaskError) {
                    console.warn('Admin Dashboard: Could not fetch team task count:', teamTaskError);
                }

                // Calculate total task count (personal + team)
                const totalTaskCount = personalTaskCount + teamTaskCount;

                // Calculate vibe badge count (unseen posts from last 24h)
                let vibeCount = 0;
                try {
                    const vibeFeedResponse = await vibeService.getFeed({
                        filter: 'company',
                        limit: 20,
                        sortBy: 'recent'
                    });

                    // Count posts from last 24 hours that haven't been seen
                    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
                    const recentPosts = vibeFeedResponse.posts?.filter(post => {
                        const postDate = new Date(post.created_at);
                        const isRecent = postDate > oneDayAgo;
                        const isUnseen = !lastVibeVisit || postDate > lastVibeVisit;
                        return isRecent && isUnseen;
                    }) || [];

                    vibeCount = recentPosts.length;
                    console.log('🎉 Admin Vibe count for badge:', vibeCount, '(unseen recent posts from last 24h)');
                } catch (vibeError) {
                    console.warn('Admin Dashboard: Could not fetch vibe feed for badge:', vibeError);
                }

                setBadgeCounts({
                    taskbox: totalTaskCount,
                    teamTaskbox: teamTaskCount,
                    vibe: vibeCount
                });

                console.log('✅ Admin Dashboard - Updated badge counts:', {
                    taskbox: totalTaskCount,
                    teamTaskbox: teamTaskCount,
                    vibe: vibeCount
                });
            } catch (error) {
                console.error('Error fetching Admin dashboard badge counts:', error);
                setBadgeCounts({ taskbox: 0, teamTaskbox: 0, vibe: 0 });
            }
        };

        // Fetch badge counts initially and when announcements change
        fetchBadgeCounts();
    }, [announcements, lastVibeVisit]);

    // Track Vibe visits
    const handleVibeVisit = () => {
        const now = new Date();
        setLastVibeVisit(now);
        localStorage.setItem('adminLastVibeVisit', now.toISOString());
        console.log('📍 Admin Dashboard: Vibe visit tracked');
    };

    const handleNavigateToVibe = (event) => {
        if (event?.stopPropagation) {
            event.stopPropagation();
        }

        handleVibeVisit();
        navigate('/admin/vibe');
    };

    // Update current date and time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentDate(new Date());
        }, 1000); // Update every second for real-time clock

        return () => clearInterval(timer);
    }, []);

    // Calculate attendance stats - now data is already processed comprehensively
    const getAttendanceStats = () => {
        const stats = {
            present: 0,
            absent: 0,
            onLeave: 0,
            total: 0,
            holiday: 0,
            weekOff: 0,
            inactive: 0
        };

        console.log('🔍 Admin Dashboard Debug - Processed todayAttendance:', todayAttendance);

        // todayAttendance now contains processed data with unique employees and correct statuses
        todayAttendance.forEach(record => {
            const status = record.status?.toLowerCase() || 'absent';
            console.log(`🔍 Processing employee: status=${status}, employee=${record.employee_name || record.first_name + ' ' + record.last_name}`);

            if (status === 'inactive') {
                stats.inactive++;
            } else {
                stats.total++; // Only count active employees

                switch(status) {
                    case 'present':
                        stats.present++;
                        break;
                    case 'absent':
                        stats.absent++;
                        break;
                    case 'leave':
                    case 'on_leave':
                        stats.onLeave++;
                        break;
                    case 'holiday':
                        stats.holiday++;
                        break;
                    case 'week_off':
                        stats.weekOff++;
                        break;
                    default:
                        stats.absent++; // Default to absent if status is unknown
                        break;
                }
            }
        });

        console.log('🔍 Admin Dashboard Debug - Final stats:', stats);
        return stats;
    };

    // Helper function to check if date is week-off for employee (same as ComprehensiveAttendanceManagement)
    const isWeekOff = (date, employee) => {
        if (!employee || !employee.weekly_off) return false;

        const parseWeeklyOff = (weeklyOffStr) => {
            if (!weeklyOffStr) return [];

            const weeklyOffStr_lower = String(weeklyOffStr).toLowerCase();
            const dayMap = {
                'sunday': 0, 'sundays': 0, 'sun': 0,
                'monday': 1, 'mondays': 1, 'mon': 1,
                'tuesday': 2, 'tuesdays': 2, 'tue': 2, 'tues': 2,
                'wednesday': 3, 'wednesdays': 3, 'wed': 3,
                'thursday': 4, 'thursdays': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
                'friday': 5, 'fridays': 5, 'fri': 5,
                'saturday': 6, 'saturdays': 6, 'sat': 6
            };

            const cleanedStr = weeklyOffStr_lower
                .replace(/\b(only|off|day|days)\b/g, '')
                .replace(/\s+/g, ' ')
                .trim();

            const parts = cleanedStr.split(/[,&]|\band\b|\s+/);
            const days = [];

            parts.forEach(part => {
                const trimmed = part.trim();
                if (trimmed && dayMap.hasOwnProperty(trimmed)) {
                    const dayNum = dayMap[trimmed];
                    if (!days.includes(dayNum)) {
                        days.push(dayNum);
                    }
                }
            });

            return days;
        };

        const weeklyOffDays = parseWeeklyOff(employee.weekly_off);
        const dayOfWeek = date.getDay();
        return weeklyOffDays.includes(dayOfWeek);
    };

    // Calculate WFH stats
    const getWFHStats = () => {
        let wfhCount = 0;
        const wfhEmployees = [];

        todayAttendance.forEach(record => {
            const locationType = record.location_type?.toLowerCase() || record.work_location?.toLowerCase() || '';

            if (locationType.includes('wfh') || locationType.includes('remote') || locationType.includes('home')) {
                wfhCount++;
                wfhEmployees.push({
                    name: record.employee_name || `${record.first_name || ''} ${record.last_name || ''}`.trim() || 'Unknown',
                    location: record.location_type || record.work_location || 'WFH'
                });
            }
        });

        return { count: wfhCount, employees: wfhEmployees };
    };

    // Calculate Late stats
    const getLateStats = () => {
        let lateCount = 0;
        let totalLateMinutes = 0;
        let maxLateEmployee = null;
        let maxLateMinutes = 0;

        todayAttendance.forEach(record => {
            if (record.is_late || record.late_by) {
                lateCount++;

                // Calculate late duration in minutes
                let lateMinutes = 0;
                if (record.late_by) {
                    // Assuming late_by is in minutes or a parseable format
                    if (typeof record.late_by === 'number') {
                        lateMinutes = record.late_by;
                    } else if (typeof record.late_by === 'string') {
                        // Try to parse string like "30 minutes" or "30"
                        const match = record.late_by.match(/(\d+)/);
                        if (match) {
                            lateMinutes = parseInt(match[1]);
                        }
                    }
                }

                totalLateMinutes += lateMinutes;

                // Track maximum late employee
                if (lateMinutes > maxLateMinutes) {
                    maxLateMinutes = lateMinutes;
                    maxLateEmployee = {
                        name: record.employee_name || `${record.first_name || ''} ${record.last_name || ''}`.trim() || 'Unknown',
                        lateBy: lateMinutes,
                        checkInTime: record.check_in_time ? (() => {
                            const { hour, minute } = getISTComponents(new Date(record.check_in_time));
                            const hour12 = hour % 12 || 12;
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            return `${hour12}:${String(minute).padStart(2, '0')} ${ampm} IST`;
                        })() : 'N/A'
                    };
                }
            }
        });

        const averageLate = lateCount > 0 ? Math.round(totalLateMinutes / lateCount) : 0;

        return {
            count: lateCount,
            averageLateMinutes: averageLate,
            maxLateEmployee
        };
    };

    const handleModuleClick = (route) => {
        if (!editMode) {
            // Track Vibe visits
            if (route === '/admin/vibe') {
                handleVibeVisit();
            }
            navigate(route);
        }
    };

    // Format date to DD/MM/YYYY (IST)
    const formatDateDisplay = (date) => {
        const { date: istDateStr } = getISTComponents(date);
        const [year, month, day] = istDateStr.split('-');
        return `${day}/${month}/${year}`;
    };

    // Get day of week (IST)
    const getDayOfWeek = (date) => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const { dayOfWeek } = getISTComponents(date);
        return days[dayOfWeek];
    };

    // Format time as HH:MM:SS (24-hour IST)
    const formatTime24Hour = (date) => {
        const { hour, minute, second } = getISTComponents(date);
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
    };

    // Format time as HH:MM:SS AM/PM (12-hour IST)
    const formatTime12Hour = (date) => {
        const { hour, minute, second } = getISTComponents(date);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')} ${ampm}`;
    };

    // Get formatted time based on current format preference
    const getFormattedTime = (date) => {
        return is24HourFormat ? formatTime24Hour(date) : formatTime12Hour(date);
    };

    // Toggle clock format
    const toggleClockFormat = () => {
        setIs24HourFormat(prev => !prev);
    };

    const handleDragStart = (e, index) => {
        setIsDragging(true);
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';

        // Add visual feedback
        if (e.target) {
            e.target.style.opacity = '0.5';
        }
    };

    const handleDragEnd = (e) => {
        if (e.target) {
            e.target.style.opacity = '';
        }
        setIsDragging(false);
        setDraggedItem(null);
        setDraggedOver(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e, index) => {
        if (draggedItem !== null && draggedItem !== index) {
            setDraggedOver(index);
        }
    };

    const handleDragLeave = (e) => {
        if (e.target === e.currentTarget) {
            setDraggedOver(null);
        }
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedItem !== null && draggedItem !== dropIndex) {
            const draggedModule = allModules[draggedItem];
            const newModules = [...allModules];

            // Remove the dragged item
            newModules.splice(draggedItem, 1);

            // Insert it at the new position
            newModules.splice(dropIndex, 0, draggedModule);

            setAllModules(newModules);
        }
        setDraggedOver(null);
    };

    // Touch handlers for mobile
    const handleTouchStart = (e, index) => {
        const touch = e.touches[0];
        setTouchStartPos({ x: touch.clientX, y: touch.clientY });

        longPressTimer.current = setTimeout(() => {
            setEditMode(true);
            setDraggedItem(index);
            // Add haptic feedback if available
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }
        }, 500); // 500ms for long press
    };

    const handleTouchMove = (e) => {
        const touch = e.touches[0];
        const moveThreshold = 10;

        // If finger moved too much, cancel long press
        if (Math.abs(touch.clientX - touchStartPos.x) > moveThreshold ||
            Math.abs(touch.clientY - touchStartPos.y) > moveThreshold) {
            if (longPressTimer.current) {
                clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
            }
        }

        // Handle drag in edit mode
        if (editMode && draggedItem !== null) {
            const touch = e.touches[0];
            const element = document.elementFromPoint(touch.clientX, touch.clientY);
            const moduleElement = element?.closest('[data-module-index]');
            if (moduleElement) {
                const index = parseInt(moduleElement.dataset.moduleIndex);
                if (index !== draggedItem && index !== draggedOver) {
                    setDraggedOver(index);
                }
            }
        }
    };

    const handleTouchEnd = (e) => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }

        // Handle drop on touch end
        if (editMode && draggedItem !== null && draggedOver !== null) {
            const draggedModule = allModules[draggedItem];
            const newModules = [...allModules];

            newModules.splice(draggedItem, 1);
            newModules.splice(draggedOver, 0, draggedModule);

            setAllModules(newModules);
            setDraggedItem(null);
            setDraggedOver(null);
        }
    };

    const resetOrder = () => {
        const defaultModules = [
            ...initialMyAccessModules,
            ...initialAdditionalModules,
            ...initialAdminAccessModules
        ];
        setAllModules(defaultModules);
        localStorage.removeItem('adminDashboardOrder'); // Clear saved order
        setEditMode(false);
    };

    const ModuleTile = ({ module, index }) => {
        // Get the correct icon component from the map
        let Icon = CheckSquare; // Default fallback

        if (module.icon && iconMap[module.icon]) {
            Icon = iconMap[module.icon];
        } else {
            console.warn(`Icon "${module.icon}" not found in iconMap, using default`);
        }

        const isDraggedOver = draggedOver === index;
        const isBeingDragged = draggedItem === index;
        const moduleTourClass = module.id === 'org-view' ? 'tour-admin-dashboard-org-view-module' : '';

        // Use exact same classes as Manager/Employee dashboards - no conditional styling for now
        return (
            <div
                data-module-index={index}
                draggable={editMode}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onTouchStart={(e) => handleTouchStart(e, index)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={() => handleModuleClick(module.route)}
                className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group ${moduleTourClass}`}
            >
                {editMode && (
                    <div className="absolute top-1 right-1 text-gray-400 dark:text-gray-500 z-10">
                        <GripVertical className="w-4 h-4" />
                    </div>
                )}
                <div className="p-6 flex flex-col items-center">
                    <div className={`${module.bgColor} p-4 rounded-2xl mb-3 group-hover:scale-110 transition-transform`}>
                        <Icon className={`w-10 h-10 ${module.iconColor}`} />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                        {module.title}
                    </span>
                    {(module.badge && Number(module.badge) > 0 && !isNaN(module.badge) && module.badge !== '0' && module.badge !== 0) ? (
                        <span className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                            {module.badge}
                        </span>
                    ) : null}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
            <div className="px-6 py-6">
                {/* Header */}
                <div className="mb-4">
                    <h1 className="tour-admin-dashboard-header text-2xl font-bold text-gray-900 dark:text-white">
                        Dashboard {editMode && <span className="text-sm text-blue-500 dark:text-blue-400">(Edit Mode)</span>}
                    </h1>
                    <p className="text-base text-gray-600 dark:text-gray-400 mt-1">
                        Welcome back, <span className="text-xl font-bold text-purple-600 dark:text-purple-400">{user?.first_name || user?.name || 'Admin'}</span>
                    </p>
                </div>

                {/* Edit Mode Controls */}
                <div className="flex justify-end items-center mb-4">
                    <div className="flex gap-2">
                        {editMode ? (
                            <>
                                <button
                                    onClick={resetOrder}
                                    className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Reset Order
                                </button>
                                <button
                                    onClick={() => setEditMode(false)}
                                    className="px-4 py-2 text-sm bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
                                >
                                    Done
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setEditMode(true)}
                                className="tour-admin-dashboard-edit-layout px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                                Edit Layout
                            </button>
                        )}
                    </div>
                </div>

                {editMode && (
                    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-lg mb-4 text-sm">
                        💡 Drag and drop widgets to reorder them. Long press on mobile devices.
                    </div>
                )}

                {/* Date and Day Display with Digital Clock */}
                <div className="tour-admin-dashboard-datetime-card bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-3 border-l-4 border-purple-500 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                {formatDateDisplay(currentDate)}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {getDayOfWeek(currentDate)}
                            </p>
                        </div>
                        <div className="text-right">
                            <p
                                onClick={toggleClockFormat}
                                className="tour-admin-dashboard-clock-toggle text-2xl font-bold text-purple-600 dark:text-purple-400 font-mono cursor-pointer hover:text-purple-700 dark:hover:text-purple-300 transition-colors select-none"
                                title="Click to toggle 12/24 hour format"
                            >
                                {getFormattedTime(currentDate)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {is24HourFormat ? '24-hour' : '12-hour'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Today's Attendance Section */}
                <div className="tour-admin-dashboard-attendance-card bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Today's Attendance</h2>
                        <button
                            onClick={() => navigate('/admin/attendance')}
                            className="tour-admin-dashboard-attendance-view-details text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                        >
                            View Details
                        </button>
                    </div>

                    {attendanceLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <>
                            {/* Attendance Stats Summary - Restructured with Active/Inactive separation */}
                            {todayAttendance.length > 0 || employees.length > 0 ? (
                                <>
                                    {/* Main Categories: Active and Inactive - Adjusted sizes */}
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                                        {/* Active Employees Box - Takes 2 columns */}
                                        <div className="lg:col-span-2 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="bg-blue-100 dark:bg-blue-800 rounded-lg p-3">
                                                    <Users className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Active Employees</h3>
                                                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                                                        {getAttendanceStats().total}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Active Employee Breakdown */}
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                                    <div className="flex items-center gap-2">
                                                        <UserCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                                                        <p className="text-xs text-gray-600 dark:text-gray-400">Present</p>
                                                    </div>
                                                    <p className="text-xl font-bold text-green-600 dark:text-green-400">
                                                        {getAttendanceStats().present}
                                                    </p>
                                                </div>

                                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                                    <div className="flex items-center gap-2">
                                                        <Users className="w-4 h-4 text-red-600 dark:text-red-400" />
                                                        <p className="text-xs text-gray-600 dark:text-gray-400">Absent</p>
                                                    </div>
                                                    <p className="text-xl font-bold text-red-600 dark:text-red-400">
                                                        {getAttendanceStats().absent}
                                                    </p>
                                                </div>

                                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                                    <div className="flex items-center gap-2">
                                                        <Plane className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                        <p className="text-xs text-gray-600 dark:text-gray-400">On Leave</p>
                                                    </div>
                                                    <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                                        {getAttendanceStats().onLeave}
                                                    </p>
                                                </div>

                                                {getAttendanceStats().weekOff > 0 && (
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                                        <div className="flex items-center gap-2">
                                                            <Home className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">Week Off</p>
                                                        </div>
                                                        <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                                                            {getAttendanceStats().weekOff}
                                                        </p>
                                                    </div>
                                                )}

                                                {getAttendanceStats().holiday > 0 && (
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                                        <div className="flex items-center gap-2">
                                                            <Award className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">Holiday</p>
                                                        </div>
                                                        <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                                                            {getAttendanceStats().holiday}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Inactive Employees Box - Takes 1 column */}
                                        <div className="lg:col-span-1 bg-gray-50 dark:bg-gray-900/20 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 flex flex-col justify-center">
                                            <div className="text-center">
                                                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 w-14 h-14 mx-auto mb-3 flex items-center justify-center">
                                                    <Users className="w-7 h-7 text-gray-600 dark:text-gray-400" />
                                                </div>
                                                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Inactive Employees</h3>
                                                <p className="text-4xl font-bold text-gray-600 dark:text-gray-400 mb-2">
                                                    {getAttendanceStats().inactive}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    Not yet joined or separated
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* WFH Employees Block */}
                                    {getWFHStats().count > 0 && (
                                        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-6">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-orange-100 dark:bg-orange-800 rounded-lg p-3">
                                                        <Home className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">Work From Home</p>
                                                        <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                                            {getWFHStats().count} {getWFHStats().count === 1 ? 'Employee' : 'Employees'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Late Arrivals Block */}
                                    {getLateStats().count > 0 && (
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="bg-yellow-100 dark:bg-yellow-800 rounded-lg p-3">
                                                    <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">Late Arrivals</p>
                                                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                                        {getLateStats().count} {getLateStats().count === 1 ? 'Employee' : 'Employees'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Average Late By</p>
                                                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                                        {getLateStats().averageLateMinutes} minutes
                                                    </p>
                                                </div>

                                                {getLateStats().maxLateEmployee && (
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Maximum Late</p>
                                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                            {getLateStats().maxLateEmployee.name}
                                                        </p>
                                                        <p className="text-xs text-red-600 dark:text-red-400">
                                                            {getLateStats().maxLateEmployee.lateBy} minutes late
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-8">
                                    <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                                    <p className="text-gray-500 dark:text-gray-400">No attendance data for today</p>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Vibe Feature Section */}
                <div className="mb-6">
                    {/* Vibe Header with Navigation */}
                    <div
                        className="tour-admin-dashboard-vibe-section flex items-center gap-3 mb-4 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg p-3 transition-colors"
                        onClick={handleNavigateToVibe}
                    >
                        <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-2">
                            <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Vibe</h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Company announcements and celebrations</p>
                        </div>
                        <div className="text-purple-600 dark:text-purple-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>

                    {/* Vibe Widgets Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Announcements Widget */}
                        <div className="tour-admin-dashboard-announcements bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow h-[400px] flex flex-col">
                            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-2">
                                    <Megaphone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Announcements</h3>
                                </div>
                                <button
                                    onClick={handleNavigateToVibe}
                                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                                >
                                    View All
                                </button>
                            </div>

                            <div className="flex-1 overflow-hidden">
                                {announcementsLoading ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : announcements.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-center p-6">
                                        <Megaphone className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                                        <p className="text-sm text-gray-500 dark:text-gray-400">No announcements at the moment</p>
                                    </div>
                                ) : (
                                    <div className="p-4 space-y-3 overflow-y-auto h-full">
                                        {announcements.map((announcement) => (
                                            <div
                                                key={announcement.id}
                                                className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                                                onClick={handleNavigateToVibe}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
                                                        <Megaphone className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                                {announcement.title}
                                                            </h4>
                                                            {announcement.priority === 'high' && (
                                                                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded flex-shrink-0">
                                                                    Important
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                                                            {announcement.message}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                            {(() => {
                                                                const { date } = getISTComponents(new Date(announcement.created_at));
                                                                const [year, month, day] = date.split('-');
                                                                return `${day}/${month}/${year}`;
                                                            })()} · {announcement.created_by_name || 'Admin'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Let's Celebrate Widget */}
                        <div className="tour-admin-dashboard-celebrations h-[400px]">
                            <CelebrationHub
                                theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                                onPostCreated={() => {
                                    // Optional: Refresh or handle post creation
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* TaskBox Feature Section */}
                <div className="mb-6">
                    {/* TaskBox Header with Navigation */}
                    <div
                        className="flex items-center gap-3 mb-4 cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg p-3 transition-colors"
                        onClick={() => navigate('/admin/taskbox')}
                    >
                        <div className="bg-green-100 dark:bg-green-900 p-2 rounded-lg">
                            <CheckSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Task Box</h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Personal and team task management</p>
                        </div>
                        <div className="text-green-600 dark:text-green-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>

                    {/* TaskBox Widgets Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Personal Tasks Widget */}
                        <div className="tour-admin-dashboard-personal-tasks">
                            <PersonalTasksWidget
                                userRole="admin"
                                dashboardView="personal"
                            />
                        </div>

                        {/* Team Tasks Widget */}
                        <div className="tour-admin-dashboard-team-tasks">
                            <TeamTasksWidget
                                userRole="admin"
                                dashboardView="team"
                            />
                        </div>
                    </div>
                </div>

                {/* All Modules Grid */}
                <div className="tour-admin-dashboard-module-grid grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {allModules.map((module, index) => (
                        <ModuleTile key={module.id} module={module} index={index} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
